import { ART_WINDOW } from '#shared/core/card-geometry';
import type { StudioPreset } from '#shared/schemas/media';

/**
 * The three preset backdrops, drawn flat in brand colors inside the art window (the caller clips
 * them to it). No gradients, no opacity tricks: every shape is a solid fill or a solid stroke.
 *
 *   rookie_card  halftone dots that grow toward the bottom
 *   streetball   a butter sun with an ink outline behind an ink chain-link fence
 *   game_cover   12 speed rays fanning out from behind the figure
 */

export const INK = '#000000';
export const INK_SOFT = '#231f20';
const BUTTER = '#ffffa1';
const PALE = '#edd0e4';
const WHITE = '#ffffff';

const W = ART_WINDOW;

const r1 = (n: number) => Math.round(n * 10) / 10;

/** Pattern tone for a window fill: white on every pastel, pale lilac on the white Rotation fill. */
export function patternTone(windowFill: string): string {
  return windowFill.toLowerCase() === WHITE ? PALE : WHITE;
}

/** The numeral inside the window: pale lilac, white where pale lilac would disappear. */
export function numeralFill(windowFill: string): string {
  return windowFill.toLowerCase() === PALE ? WHITE : PALE;
}

/**
 * Halftone: a staggered grid of dots, radius 1.5 at the top growing to 6.5 at the bottom of the
 * window. One path per radius step keeps the DOM small. Returns the path data per radius.
 */
export function halftonePaths(
  spacing = 24,
  rect: { x: number; y: number; w: number; h: number } = W,
  rMin = 1.5,
  rSpan = 5,
): { r: number; d: string }[] {
  const rows = Math.ceil(rect.h / (spacing * 0.866)) + 1;
  const cols = Math.ceil(rect.w / spacing) + 1;
  const byR = new Map<number, string>();
  for (let j = 0; j < rows; j++) {
    const y = rect.y + j * spacing * 0.866;
    const t = Math.min(1, Math.max(0, (y - rect.y) / rect.h));
    const r = Math.round((rMin + t * rSpan) * 2) / 2;
    const off = j % 2 === 0 ? 0 : spacing / 2;
    for (let i = 0; i < cols; i++) {
      const x = rect.x + off + i * spacing;
      byR.set(r, `${byR.get(r) ?? ''}M${r1(x - r)},${r1(y)}a${r},${r} 0 1,0 ${r * 2},0a${r},${r} 0 1,0 ${-r * 2},0Z`);
    }
  }
  return [...byR.entries()].map(([r, d]) => ({ r, d }));
}

/** Two sets of diagonals, 34 apart: the fence. One path. */
export function chainLinkPath(step = 34): string {
  const len = W.w + W.h;
  const parts: string[] = [];
  for (let c = -W.h; c <= W.w; c += step) {
    parts.push(`M${r1(W.x + c)},${r1(W.y + W.h)}l${len},${-len}`);
    parts.push(`M${r1(W.x + c)},${r1(W.y)}l${len},${len}`);
  }
  return parts.join('');
}

/** `count` rays fanning from (cx, cy): each a wedge `halfDeg` degrees wide on both sides of its axis. */
export function rayWedges(cx: number, cy: number, count = 12, startDeg = 7.5, halfDeg = 7.5, radius = 900): string[] {
  return Array.from({ length: count }, (_, i) => {
    const mid = startDeg + (360 / count) * i;
    const a0 = ((mid - halfDeg) * Math.PI) / 180;
    const a1 = ((mid + halfDeg) * Math.PI) / 180;
    const p = (a: number) => `${r1(cx + Math.cos(a) * radius)},${r1(cy + Math.sin(a) * radius)}`;
    return `M${cx},${cy}L${p(a0)}L${p(a1)}Z`;
  });
}

export interface BackdropProps {
  preset: StudioPreset;
  /** The window fill, hex. */
  fill: string;
}

/** The backdrop drawn over the window fill. Coordinates are card coordinates. */
export function Backdrop({ preset, fill }: BackdropProps) {
  const tone = patternTone(fill);
  if (preset === 'streetball') {
    return (
      <g>
        <circle cx={368} cy={150} r={92} fill={BUTTER} stroke={INK} strokeWidth={6} />
        <path d={chainLinkPath()} fill="none" stroke={INK_SOFT} strokeWidth={3} strokeLinecap="butt" />
      </g>
    );
  }
  if (preset === 'game_cover') {
    return (
      <g fill={tone}>
        {rayWedges(W.x + W.w / 2, 330).map((d) => (
          <path key={d} d={d} />
        ))}
      </g>
    );
  }
  return (
    <g fill={tone}>
      {halftonePaths().map(({ r, d }) => (
        <path key={r} d={d} />
      ))}
    </g>
  );
}
